<template>
    <div class="min-h-[350px] relative">
        <!-- Overlay -->
        <Transition name="overlay">
            <div 
                v-if="isDrawerOpen" 
                class="fixed inset-0 bg-echoes-background-black/70 z-40"
                @click="toggleDrawer"
            ></div>
        </Transition>

        <!-- Drawer -->
        <Transition name="drawer">
            <div 
                v-if="isDrawerOpen"
                class="fixed bottom-0 right-0 h-2/3 w-80 bg-echoes-background-gray shadow-2xl z-50 overflow-y-auto pb-3 rounded-tl-lg"
            >   
                <div class="flex flex-col h-full justify-between">
                    <div>
                        <div class="p-6 border-b border-echoes-background-black">
                            <div class="flex justify-between items-center">
                                <h3 class="text-xl font-bold text-white">Filters</h3>
                                <button 
                                    @click="toggleDrawer"
                                    class="text-white hover:text-gray-700 transition-colors"
                                >
                                    <i class="uil uil-times text-2xl"></i>
                                </button>
                            </div>
                            <div v-if="filtersSelected.length > 0">
                                <div class="flex items-center gap-2 flex-wrap mt-2">
                                    <div v-for="filter in filtersSelected" :key="filter.id" @click="removeFilter(filter)">
                                        <div v-if="filter.color" class="inline-flex items-center px-3 py-1 text-sm rounded-full whitespace-nowrap shadow-md text-white gap-2" :class="getStatusStyle(filter.color)">
                                            {{ filter.value }} <i class="uil uil-times"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
    
                        <div>
                            <div v-for="option in filterOptions" :key="option.column" class="w-full px-6 py-2">
                                <div class="text-white text-lg font-bold pb-3">{{ option.column }}</div>
                                <div v-if="option.type === 'tag'" class="flex items-center gap-2 flex-wrap">
                                    <div v-for="opt in option.options" :key="opt.id" @click="addFilter(opt)">
                                        <div class="font-medium tracking-wider inline-flex items-center px-3 py-1 text-sm rounded-full whitespace-nowrap shadow-md text-white" :class="filtersSelected.some(filter => filter.id === opt.id) ? 'bg-echoes-background-black/30 text-gray-400' : getStatusStyle(opt.color)">
                                        {{ opt.value }}
                                    </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
    
                    <div class="pt-2 px-2 border-t border-echoes-background-black flex gap-2 justify-end">
                        <button class="bg-echoes-background-black rounded-xl p-2 text-white transition-colors cursor-pointer flex items-center gap-2" @click="clearFilters">
                            Clear
                        </button>
                        <button class="rounded-xl p-2 text-white transition-colors flex items-center gap-2" :class="filtersSelected.length > 0 ? 'bg-echoes-primary' : 'bg-echoes-background-black/30 text-gray-400'" @click="toggleDrawer">
                            See Results
                        </button>
                    </div>
                </div>

            </div>
        </Transition>

        <div v-if="filteredItems.length === 0 && !isLoading" class="text-center text-gray-400">No results found</div >

        <div v-else-if="isLoading" class="flex flex-col justify-center items-center min-h-[350px]">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-echoes-primary"></div>
        </div>

        <div v-else>
            <div class="w-full mb-4 flex items-center" :class="showFilters ? 'justify-around' : 'justify-start'">
                <SelectInput
                    v-model="order"
                    :options="orderOptions" 
                    id="order"
                    placeholder="Select order" 
                    class="w-1/2"
                ></SelectInput>
                <button 
                    v-if="showFilters"
                    @click="toggleDrawer"
                    class="bg-echoes-primary rounded-xl px-8 py-2 text-white hover:bg-echoes-primary/90 transition-colors cursor-pointer flex items-center gap-2"
                >
                    <i class="uil uil-filter"></i>
                    Filter
                </button>
            </div>
            <div class="card-table bg-echoes-background-gray flex flex-col pt-2 rounded-lg space-y-2 mb-4" v-for="item in filteredItems" :key="item.id">
                <div class="flex justify-between items-center px-2 border-b border-echoes-background-black pb-2">
                    <div class="text-lg font-bold w-full truncate pr-4">{{ item.title }}</div>
                    <div class="flex gap-4 text-xl">
                        <button v-if="props.canDelete" @click="emit('delete', item.id)"><i class="uil uil-trash-alt"></i></button>
                        <button v-if="props.canEdit" @click="emit('edit', item.id)"><i class="uil uil-pen text-echoes-primary"></i></button>
                    </div>
                </div>
                <div class="flex flex-col gap-2 pb-4 px-2">
                    <div class="grid grid-cols-3 w-full text-sm px-1" 
                        v-for="(row, index) in item.rows" 
                        :key="row.column"
                        :class="index % 2 === 0 ? 'bg-transparent' : 'bg-white/5'">
                        <div class="col-span-1">{{ row.column }}</div>
                        <div class="col-span-2" v-if="row.type === 'tag'">
                            <div class="inline-flex items-center px-2  text-xs rounded-full whitespace-nowrap shadow-md text-white" :class="getStatusStyle(row.color)">
                                {{ row.value }}
                            </div>
                        </div>
                        <div class="col-span-2" v-else-if="row.type === 'date'">
                            {{ formatDate(row.value) }}
                        </div>
                        <div class="col-span-2" v-else>{{ row.value }}</div>
                    </div>
                </div>
                <button v-if="props.hasAction" 
                    class="rounded-b-lg px-2 py-1 text-center" 
                    :disabled='!item.actionEnabled' 
                    @click="emit('action', item.id)"
                    :class="item.actionEnabled ? 'bg-echoes-primary' : 'bg-echoes-background-black/30 text-gray-400'">{{ props.hasActionText }}</button>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref, defineProps, defineEmits, computed } from 'vue';
import { useLayoutComponents } from '@/composables/layout';
const { SelectInput } = useLayoutComponents();

const props = defineProps({
    items: {
        type: Array,
        required: true,
    },
    canEdit: {
        type: Boolean,
        required: false,
        default: false,
    },
    canDelete: {
        type: Boolean,
        required: false,
        default: false,
    },
    hasAction: {
        type: Boolean,
        required: false,
        default: false,
    },
    hasActionText: {
        type: String,
        required: false,
        default: 'Submit',
    },
    quickFilterText: {
        type: String,
        required: false,
        default: '',
    },
    isLoading: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const order = ref('default')
const orderOptions = computed(() => getOrderOptions())
const showFilters = computed(() => {
    return props.items[0].rows.some(row => row.type === 'tag')
})
const filterOptions = computed(() => {
    const options = []
    props.items.forEach(item => {
        item.rows.forEach(row => {
            if(row.type === 'tag') {
                if(!options.some(option => option.column === row.column)) {
                    options.push({
                        column: row.column,
                        type: row.type,
                        options: [
                            {id: `${row.column}-${row.value}`, value: row.value, color: row.color},
                        ]
                    })
                } else {
                    const existingOption = options.find(option => option.column === row.column);
                    const isDuplicate = existingOption.options.some(opt => opt.id === `${row.column}-${row.value}`);
                    if (!isDuplicate) {
                        existingOption.options.push({id: `${row.column}-${row.value}`, value: row.value, color: row.color});
                    }
                }
            }
        })
    })
    return options
})
const filtersSelected = ref([])
const isDrawerOpen = ref(false)

const toggleDrawer = () => {
    isDrawerOpen.value = !isDrawerOpen.value
}

const addFilter = (option) => {
    if(!filtersSelected.value.some(filter => filter.id === option.id)) {
        filtersSelected.value.push(option)
    }
}

const removeFilter = (option) => {
    filtersSelected.value = filtersSelected.value.filter(filter => filter.id !== option.id)
}

const clearFilters = () => {
    filtersSelected.value = []
}

const getFilters = () => {
    let filters = [];
    filtersSelected.value.forEach(filter => {
        const [column, value] = filter.id.split('-')
        if(filters[column]) {
            filters[column].push(value)
        } else {
            filters[column] = [value]
        }
    })
    return filters
}

const emit = defineEmits(['edit', 'delete', 'action']);

const getStatusStyle = (type) => {
    const styles = {
        'success': 'bg-badge-green/20 text-badge-green border border-badge-green',
        'warning': 'bg-badge-yellow/20 text-badge-yellow border border-badge-yellow',
        'error': 'bg-badge-red/20 text-badge-red border border-badge-red',
        'info': 'bg-badge-blue/20 text-badge-blue border border-badge-blue',
    };
    return styles[type] || 'bg-gray-900 text-gray-300';
};

const reorderItems = (items, orderValue) => {
    if (!orderValue || orderValue === 'default') return items;
    
    const [column, direction] = orderValue.split('/');
    const sortedItems = [...items];
    
    if (column === 'title') {
        return sortedItems.sort((a, b) => {
            const comparison = a.title.localeCompare(b.title);
            return direction === 'asc' ? comparison : -comparison;
        });
    } else {
        return sortedItems.sort((a, b) => {
            const aRow = a.rows.find(row => row.column === column);
            const bRow = b.rows.find(row => row.column === column);
            
            if (!aRow || !bRow) return 0;
            
            let aValue = aRow.value;
            let bValue = bRow.value;
            
            if (aRow.type === 'date') {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
                const comparison = aValue.getTime() - bValue.getTime();
                return direction === 'asc' ? comparison : -comparison;
            }
            
            // Para outros tipos, usar comparação de string
            const comparison = String(aValue).localeCompare(String(bValue));
            return direction === 'asc' ? comparison : -comparison;
        });
    }
};

const filteredItems = computed(() => {
    let filtered = props.items.filter(item => 
        item.title?.toLowerCase().includes(props.quickFilterText?.toLowerCase()) || 
        item.rows.some(row => 
            String(row.value)?.toLowerCase().includes(props.quickFilterText?.toLowerCase())
        )
    );
    const filters = getFilters()
    filtered = filtered.filter(item => {
        let result = true
        Object.entries(filters).forEach(([column, values]) => {
            const matchingRow = item.rows.find(row => row.column === column)
            if (!matchingRow) {
                result = false
                return
            }
            const rowValue = matchingRow.value.toLowerCase()
            result = result && values.some(value => rowValue === value.toLowerCase())
        })
        return result
    })
    return reorderItems(filtered, order.value);
});

const formatDate = (utcDate) => {
    if (!utcDate) return '';
    const date = new Date(utcDate);
    
    const dateStr = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });

    const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
    });

    return `${dateStr} at ${timeStr} ET`;
};

const getOrderOptions = () => {
    let options = [
        {label: 'Default Order', value: 'default'},
        {label: 'Name (A-Z)', value: 'title/asc'},
        {label: 'Name (Z-A)', value: 'title/desc'},
    ];

    if (filteredItems.value.length > 0) {
        filteredItems.value[0].rows.forEach(row => {
            if (row.type === 'date') {
                options.push({
                    label: `${row.column} (Newest)`,
                    value: `${row.column}/desc`
                });
                options.push({
                    label: `${row.column} (Oldest)`,
                    value: `${row.column}/asc`
                });
            }
        });
    }
    
    return options;
};

</script>   

<style scoped>
.card-table {
    animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.drawer-enter-active, .drawer-leave-active {
    transition: transform 0.3s ease-in-out;
}

.drawer-enter-from {
    transform: translateX(100%);
}

.drawer-enter-to {
    transform: translateX(0);
}

.drawer-leave-from {
    transform: translateX(0);
}

.drawer-leave-to {
    transform: translateX(100%);
}

/* Overlay Animations */
.overlay-enter-active, .overlay-leave-active {
    transition: opacity 0.3s ease-in-out;
}

.overlay-enter-from, .overlay-leave-to {
    opacity: 0;
}

.overlay-enter-to, .overlay-leave-from {
    opacity: 1;
}
</style>